/**
 * Materials-feature data layer + i18n namespace registration.
 *
 * The signature dual-pane Materials screen
 * (`docs/GardEn/handoff-2026-06/garden/screens-materials.jsx`) consumes the
 * design-revamp quotes endpoint: `GET /quotes/{id}` when an `:id` route param
 * is present, else `POST /quotes` (the demo extractor that reproduces Смета
 * №142). Both return the full `QuoteResponse` (header + materials + matches +
 * summary). The fetchers themselves live in the shared `@/lib/garden-api`
 * (already created); this module wraps them in TanStack Query hooks and owns
 * the `materials` i18n namespace.
 *
 * The `materials` namespace is registered here via `i18n.addResourceBundle`
 * (NOT in `apps/web/src/lib/i18n.ts`, which is not in this screen's
 * allowed-touch list) — the same pattern `features/account/api.ts` uses. All
 * three locales (ru/ky/en) ship per ADR-028 with the documented RU-fallback
 * chain (REQ-PRD-§13.2-A).
 *
 * No PII travels here: the quote payload deliberately omits the contractor
 * address/phone (only the quote number + contractor name + date render), and
 * seller matches carry only business name + price.
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import i18n from "@/lib/i18n";
import {
  createQuote,
  getQuote,
  type MaterialConfidence,
  type QuoteResponse,
} from "@/lib/garden-api";
import type { GConfidenceLevel } from "@garden/ui";

import materialsEn from "@/locales/en/materials.json";
import materialsKy from "@/locales/ky/materials.json";
import materialsRu from "@/locales/ru/materials.json";

export const MATERIALS_NS = "materials";

let materialsI18nRegistered = false;

/**
 * Register the `materials` i18n namespace (ru/ky/en) on the shared i18next
 * singleton. Idempotent + init-safe (mirrors `registerAccountI18n`): guarded on
 * `i18n.isInitialized` so a call before `initI18n()` is a no-op, and on a
 * module-level flag so repeated screen renders don't re-add the bundle. The
 * 4th arg `deep=true` merges nested objects; the 5th `overwrite=false` keeps an
 * existing bundle (HMR-safe). Called at the top of the screen's render so the
 * keys are present regardless of import order.
 */
export function registerMaterialsI18n(): void {
  if (materialsI18nRegistered) return;
  if (!i18n.isInitialized || typeof i18n.addResourceBundle !== "function") {
    return;
  }
  i18n.addResourceBundle("ru", MATERIALS_NS, materialsRu, true, false);
  i18n.addResourceBundle("ky", MATERIALS_NS, materialsKy, true, false);
  i18n.addResourceBundle("en", MATERIALS_NS, materialsEn, true, false);
  materialsI18nRegistered = true;
}

// Best-effort eager registration for the production path (initI18n() runs
// before any feature module imports in main.tsx). In tests where init happens
// in beforeEach, the screen calls registerMaterialsI18n() on render.
registerMaterialsI18n();

/**
 * Map the API confidence token (`high | med | low`) to the `ConfidenceBadge`
 * level union (`high | medium | low`). The badge renames `med` → `medium`
 * (GConfidence story); an unexpected value falls back to `medium` so the badge
 * always renders a defined tone.
 */
export function confidenceToLevel(conf: MaterialConfidence): GConfidenceLevel {
  if (conf === "high") return "high";
  if (conf === "low") return "low";
  return "medium";
}

/** Query keys: a real quote id is `['quote', id]`; the demo create is
 * `['quote', 'current']` (one per session). */
export function quoteQueryKey(quoteId: string | undefined): readonly unknown[] {
  return ["quote", quoteId ?? "current"];
}

/** staleTime — the demo quote is immutable once extracted; keep it warm so a
 * single session creates exactly one (avoids re-POSTing on remount). */
export const QUOTE_STALE_TIME_MS = 5 * 60 * 1000;

/**
 * Fetch the current quote.
 *
 * With a route `:id` it does `GET /quotes/{id}` (cache key `['quote', id]`).
 * Without one it `POST /quotes` (the demo extractor; cache key
 * `['quote', 'current']`) and caches the result so the session creates exactly
 * one quote even across remounts. The POST is idempotent server-side
 * (Idempotency-Key handled by the api-client), so a retry is safe.
 */
export function useQuote(quoteId?: string): UseQueryResult<QuoteResponse, Error> {
  return useQuery<QuoteResponse, Error>({
    queryKey: quoteQueryKey(quoteId),
    queryFn: (): Promise<QuoteResponse> =>
      quoteId !== undefined ? getQuote(quoteId) : createQuote(),
    staleTime: QUOTE_STALE_TIME_MS,
  });
}
